import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";

const CompletedComplaints = () => {
    const { user, loading: userLoading, users, usersLoading } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [filteredComplaints, setFilteredComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;
    const statusOptions = ["completed", "paused", "in-operation"];

    const technicians = useMemo(() => {
        return users.filter((u) => u.role === "technician")
    }, [users]);

    console.log("technicians :", technicians)
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                let data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Show only completed and paused complaints
                data = data.filter(
                    (c) => c.status === "completed" || c.status === "paused"
                );

                // Role-based filtering
                if (user?.role === "technician") {
                    data = data.filter((c) => c.assignedTo === user.uid);
                }

                // Sort newest first
                data.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);

                setComplaints(data);
                setFilteredComplaints(data);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load completed complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Filter by status
    useEffect(() => {
        if (filterStatus === "all") setFilteredComplaints(complaints);
        else
            setFilteredComplaints(
                complaints.filter((c) => c.status === filterStatus)
            );
        setCurrentPage(1);
    }, [filterStatus, complaints]);

    const handleStatusChange = async (complaintId, newStatus) => {
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                status: newStatus,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `Status changed to ${newStatus}`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success(`Status updated to ${newStatus}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };

    if (loading || userLoading || usersLoading) return <Loading />;
    if (!filteredComplaints.length)
        return <p className="text-center mt-6">No completed or paused complaints found.</p>;

    // Pagination
    const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);
    const paginated = filteredComplaints.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Completed & Paused Complaints</h2>
                <div className="flex items-center gap-3">
                    <span className="font-medium">Filter:</span>
                    <CustomSelect
                        name="filterStatus"
                        options={["all", "completed", "paused"]}
                        value={filterStatus}
                        onChange={(name, value) => setFilterStatus(value)}
                    />
                </div>
            </div>

            <table className="w-full border-collapse border border-gray-300">
                <thead>
                    <tr className="bg-gray-100 text-sm">
                        <th className="border p-2">Ticket</th>
                        <th className="border p-2">Customer</th>
                        <th className="border p-2">Phone</th>
                        <th className="border p-2">Department</th>
                        <th className="border p-2">Assigned To</th>
                        <th className="border p-2">Status</th>
                        <th className="border p-2">Resolved At</th>
                        <th className="border p-2">Comment</th>
                        {user?.role === "admin" && <th className="border p-2">Action</th>}
                    </tr>
                </thead>
                <tbody>
                    {paginated.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50 text-sm">
                            <td className="border p-2 text-center">{c.ticketNumber}</td>
                            <td className="border p-2 text-center">{c.customerInfo?.name}</td>
                            <td className="border p-2 text-center">{c.customerInfo?.phone}</td>
                            <td className="border p-2 text-center">{c.department}</td>
                            <td className="border p-2 text-center">
                                {/* {c.assignedTo || "Unassigned"} */}
                                {technicians.find((t) => t.uid === c.assignedTo)?.name || "Unassigned"}
                            </td>
                            <td className="border p-2 text-center capitalize">{c.status}</td>
                            <td className="border p-2 text-center">
                                {c.resolvedAt
                                    ? new Date(c.resolvedAt.seconds * 1000).toLocaleString()
                                    : "—"}
                            </td>

                            <td className="border p-2 text-center">
                                {c.completionComment || "Unassigned"}
                            </td>

                            {user?.role === "admin" && (
                                <td className="border p-2 text-center">
                                    <CustomSelect
                                        name="status"
                                        placeholder="Change Status"
                                        options={statusOptions}
                                        value={c.status}
                                        onChange={(name, value) =>
                                            handleStatusChange(c.id, value)
                                        }
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-between items-center pt-4">
                <p className="text-sm text-gray-600">
                    Showing {paginated.length} of {filteredComplaints.length} complaints
                </p>
                <div className="flex gap-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Prev
                    </button>
                    <span className="text-sm font-medium">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompletedComplaints;
